import Container from "../layout/Container";
import logo from "@/assets/logos/zenia_stays_transparent.svg";
import { Instagram } from "lucide-react";

export default function Footer() {
  return (
    <footer className="w-full bg-bg-offwhite text-text-primary border-t border-border-soft">

      {/* TOP CTA */}
      <div className="w-full py-14 md:py-18">
        <Container>

          <div className="grid gap-8 lg:grid-cols-[1.2fr_0.8fr] items-center">

            {/* LEFT */}
            <div className="max-w-content">

              <p className="text-small uppercase tracking-[0.22em] text-brand-primary font-semibold">
                Zenia Stays
              </p>

              <h2 className="
                mt-3 font-heading font-extrabold
                text-[24px] sm:text-[30px] lg:text-[40px]
                leading-[1.08]
              ">
                Premium Airbnb management built for performance-driven owners.
              </h2>

              <p className="mt-4 text-body text-text-muted max-w-reading">
                We manage pricing, guest communication, operations, and reporting so your property performs like a business.
              </p>

            </div>

            {/* RIGHT */}
            <div className="flex lg:justify-end">

              <div className="flex flex-col sm:flex-row gap-3">

                <a href="/contact" className="rounded-pill bg-brand-primary px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-brand-secondary">
                  Let&apos;s connect
                </a>

                <a href="/solutions" className="rounded-pill border border-border-soft bg-bg-light px-5 py-2.5 text-sm font-semibold hover:bg-bg-offwhite transition">
                  Explore solutions
                </a>

              </div>

            </div>

          </div>

        </Container>
      </div>

      <div className="w-full h-px bg-border-soft" />

      {/* MAIN */}
      <div className="w-full py-12 md:py-14">
        <Container>

          <div className="
            grid gap-10
            grid-cols-2
            lg:grid-cols-[1.1fr_0.9fr_0.9fr_0.9fr]
            items-start
          ">

            {/* LEFT BLOCK */}
            <div className="col-span-2 lg:col-span-1 max-w-content">

              <div className="flex items-center gap-3">

                <div className="h-10 flex items-center">
                  <img src={logo} alt="Zenia Stays" className="h-[52px] md:h-[60px] w-auto" />
                </div>

                <div>
                  <div className="font-heading text-[20px] font-extrabold leading-none">
                    Zenia Stays
                  </div>
                  <div className="mt-1 text-small text-text-muted">
                    Premium property management
                  </div>
                </div>

              </div>

              <p className="mt-5 text-small text-text-muted leading-relaxed max-w-reading">
                Built for owners who want consistent occupancy, better guest experience, and complete operational control.
              </p>

            </div>

            {/* COLUMNS */}
            <FooterCol title="Explore">
              <FooterLink href="/">Home</FooterLink>
              <FooterLink href="/solutions">Solutions</FooterLink>
              <FooterLink href="/revenue-calc">Calculator</FooterLink>
              <FooterLink href="/book">Book stay</FooterLink>
            </FooterCol>

            <FooterCol title="Company">
              <FooterLink href="/solutions">About</FooterLink>
              <FooterLink href="#">Case studies</FooterLink>
              <FooterLink href="/privacy">Privacy</FooterLink>
              <FooterLink href="/terms">Terms</FooterLink>
            </FooterCol>

            <FooterCol title="Contact">
              <FooterLink href="/contact">Contact us</FooterLink>
              <FooterLink href="mailto:hello@zeniastays.com">hello@zeniastays.com</FooterLink>
              <FooterLink href="/revenue-calc">Revenue calculator</FooterLink>
              <FooterLink href="/contact">Partner with us</FooterLink>
            </FooterCol>

          </div>

        </Container>
      </div>

      <div className="w-full h-px bg-border-soft" />

      {/* BOTTOM */}
      <div className="w-full py-6 md:py-7">
        <Container>

          <div className="
            flex flex-col md:flex-row
            items-center justify-between
            gap-3
            text-center md:text-left
          ">

            <p className="text-small text-text-muted">
              © {new Date().getFullYear()} Zenia Stays
            </p>

            <p className="text-small text-text-muted flex items-center justify-center md:justify-end gap-2">

              <span className="hidden md:inline">
                Built for performance-focused property owners
              </span>

              <a
                href="https://instagram.com/zeniastays"
                target="_blank"
                rel="noopener noreferrer"
                className="
                  flex items-center gap-1.5
                  transition-all duration-normal
                  hover:scale-[1.05]
                "
              >
                <Instagram
                  size={18}
                  className="
                    text-pink-500
                    transition-all duration-normal
                    hover:text-pink-600
                  "
                />

                <span className="
                  md:hidden
                  font-medium
                  bg-gradient-to-r from-pink-500 to-orange-400
                  bg-clip-text text-transparent
                ">
                  @zeniastays
                </span>
              </a>

            </p>
          </div>

        </Container>
      </div>

    </footer>
  );
}

function FooterCol({ title, children }) {
  return (
    <div className="flex flex-col gap-3 min-w-[120px]">
      <span className="text-small font-semibold uppercase tracking-[0.18em] text-brand-primary">
        {title}
      </span>
      {children}
    </div>
  );
}

function FooterLink({ href, children }) {
  return (
    <a href={href} className="text-small text-text-muted hover:text-text-primary transition">
      {children}
    </a>
  );
}